import { useEffect, useMemo, useState } from "react";
import { ChevronRight, FileSignature, Search } from "lucide-react";
import { Link } from "react-router-dom";

import { api } from "../../api/client";
import { jdDate } from "../../utils/jobDescription";

interface Person {
  employee_id: number;
  name: string;
  job_title: string | null;
  site_name: string | null;
  manager_name: string | null;
  status: "none" | "issued" | "changes_requested" | "signed";
  version: number | null;
  issued_at: string | null;
  signed_at: string | null;
  has_draft: boolean;
  draft_ready: boolean;
  has_uploaded: boolean;
}

interface IssueAllResult {
  issued: { employee_id: number; name: string; version: number; withdrawn: string[]; sent: boolean }[];
  skipped: { employee_id: number; name: string; reason: string }[];
  emails_sent: number;
  email_paused: boolean;
}

type Filter = "all" | "none" | "issued" | "changes_requested" | "signed";

const STATUS: Record<Person["status"], [string, string]> = {
  none: ["Not in PME People", "bg-gray-100 text-gray-600"],
  issued: ["Waiting for signature", "bg-blue-50 text-pme-blue"],
  changes_requested: ["Change asked for", "bg-amber-50 text-amber-800"],
  signed: ["Signed", "bg-green-50 text-green-700"],
};

/**
 * Admin → Performance → Job descriptions: where everyone's job description
 * stands now that they are written and signed in PME People.
 */
export default function JobDescriptionsOverview() {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [confirmIssueAll, setConfirmIssueAll] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [issueResult, setIssueResult] = useState<IssueAllResult | null>(null);
  const [issueError, setIssueError] = useState("");

  const load = async () => {
    try {
      setPeople((await api.get<{ people: Person[] }>("/api/job-descriptions/overview")).people);
    } catch {
      setPeople([]);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const issueAll = async () => {
    setIssuing(true);
    setIssueError("");
    try {
      const result = await api.post<IssueAllResult>("/api/job-descriptions/issue-ready", {});
      setIssueResult(result);
      setConfirmIssueAll(false);
      await load();
    } catch (e) {
      setIssueError(e instanceof Error ? e.message : "Something went wrong — nothing was issued.");
    } finally {
      setIssuing(false);
    }
  };

  const counts = useMemo(() => {
    const c = { all: 0, none: 0, issued: 0, changes_requested: 0, signed: 0 } as Record<Filter, number>;
    for (const p of people ?? []) {
      c.all += 1;
      c[p.status] += 1;
    }
    return c;
  }, [people]);

  if (people == null) return <p className="text-sm text-gray-400">Loading job descriptions...</p>;

  const q = query.trim().toLowerCase();
  const shown = people.filter((p) => {
    if (filter !== "all" && p.status !== filter) return false;
    return !q || `${p.name} ${p.job_title ?? ""} ${p.manager_name ?? ""} ${p.site_name ?? ""}`.toLowerCase().includes(q);
  });

  const chips: { key: Filter; label: string }[] = [
    { key: "all", label: "Everyone" },
    { key: "none", label: "Not in PME People" },
    { key: "issued", label: "Waiting for signature" },
    { key: "changes_requested", label: "Change asked for" },
    { key: "signed", label: "Signed" },
  ];

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
        <p className="flex items-center gap-1.5 text-sm font-semibold"><FileSignature size={14} aria-hidden /> Job descriptions</p>
        <p className="mt-1">
          Line managers write and change job descriptions in PME People, then issue them for the employee to sign.
          "Uploaded file" means an old job description document is still on their record; open the person to read it in.
        </p>
      </div>

      {(() => {
        const ready = (people ?? []).filter((p) => p.draft_ready);
        const notReady = (people ?? []).filter((p) => p.has_draft && !p.draft_ready);
        if (ready.length === 0 && !issueResult) return null;
        return (
          <div className="rounded-lg border border-pme-blue/30 bg-pme-sky/60 p-3 text-xs text-pme-navy">
            {ready.length > 0 && (
              <>
                <p className="text-sm font-semibold">
                  {ready.length === 1 ? "1 draft is ready to issue" : `${ready.length} drafts are ready to issue`}
                </p>
                <p className="mt-1">
                  Issuing sends each job description to the person to sign and withdraws their old unsigned
                  uploaded job description files. Signed files are never touched.
                  {notReady.length > 0 && ` ${notReady.length} other draft${notReady.length === 1 ? " is" : "s are"} not finished yet and will be left alone.`}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {!confirmIssueAll ? (
                    <button type="button" onClick={() => setConfirmIssueAll(true)}
                      className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-pme-navy">
                      Issue all {ready.length} for signing
                    </button>
                  ) : (
                    <>
                      <span className="font-semibold">
                        Issue {ready.length === 1 ? ready[0].name + "'s job description" : `${ready.length} job descriptions`} now?
                      </span>
                      <button type="button" onClick={() => void issueAll()} disabled={issuing}
                        className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-pme-navy disabled:opacity-60">
                        {issuing ? "Issuing..." : "Yes, issue them"}
                      </button>
                      <button type="button" onClick={() => setConfirmIssueAll(false)} disabled={issuing}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
            {issueError && <p className="mt-2 font-semibold text-red-700">{issueError}</p>}
            {issueResult && (
              <div className="mt-2 rounded-lg border border-pme-blue/20 bg-white p-2.5">
                <p className="text-sm font-semibold text-green-700">
                  {issueResult.issued.length === 1
                    ? "1 job description issued for signing."
                    : `${issueResult.issued.length} job descriptions issued for signing.`}
                </p>
                {issueResult.email_paused ? (
                  <p className="mt-1 text-amber-800">
                    Email is paused, so nobody was emailed. Each person will see it on their home page when they sign in.
                  </p>
                ) : (
                  <p className="mt-1 text-gray-600">{issueResult.emails_sent} email{issueResult.emails_sent === 1 ? "" : "s"} sent.</p>
                )}
                {issueResult.issued.some((p) => p.withdrawn.length > 0) && (
                  <p className="mt-1 text-gray-600">
                    Old uploaded files withdrawn: {issueResult.issued.filter((p) => p.withdrawn.length > 0)
                      .map((p) => `${p.name} (${p.withdrawn.length})`).join(", ")}.
                  </p>
                )}
                {issueResult.skipped.length > 0 && (
                  <div className="mt-1.5">
                    <p className="font-semibold text-amber-800">Not issued — the draft isn't finished:</p>
                    <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-gray-600">
                      {issueResult.skipped.map((s) => (
                        <li key={s.employee_id}>
                          <Link to={`/job-description/${s.employee_id}`} className="font-semibold text-pme-blue hover:underline">{s.name}</Link>
                          {" — "}{s.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button type="button" onClick={() => setIssueResult(null)}
                  className="mt-2 text-[11px] font-semibold text-gray-500 hover:text-gray-700">
                  Dismiss
                </button>
              </div>
            )}
          </div>
        );
      })()}

      <div className="flex flex-wrap items-center gap-2">
        {chips.map((c) => (
          <button key={c.key} type="button" onClick={() => setFilter(c.key)} aria-pressed={filter === c.key}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === c.key ? "bg-pme-blue text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            {c.label} ({counts[c.key]})
          </button>
        ))}
        <label className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 sm:ml-auto sm:w-auto sm:min-w-[240px]">
          <Search size={14} className="text-gray-400" aria-hidden />
          <span className="sr-only">Find a person, manager or site</span>
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a person, manager or site"
            className="min-h-[36px] w-full bg-transparent text-sm outline-none" />
        </label>
      </div>

      {shown.length === 0 ? (
        <p className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">Nobody matches.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
          {shown.map((p) => {
            const [label, cls] = STATUS[p.status];
            const when = p.status === "signed" ? jdDate(p.signed_at) : p.issued_at ? jdDate(p.issued_at) : "";
            return (
              <li key={p.employee_id}>
                <Link to={`/job-description/${p.employee_id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 hover:bg-gray-50">
                  <span className="min-w-0 flex-1 basis-56">
                    <span className="block text-sm font-semibold text-gray-900">{p.name}</span>
                    <span className="block text-xs text-gray-500">
                      {[p.job_title, p.site_name, p.manager_name ? `Manager: ${p.manager_name}` : null].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
                    {label}{p.version ? ` · v${p.version}` : ""}
                  </span>
                  {when && <span className="text-xs text-gray-500">{when}</span>}
                  {p.has_draft && (
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.draft_ready ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                      {p.draft_ready ? "Draft · ready to issue" : "Draft · not finished"}
                    </span>
                  )}
                  {p.status === "none" && p.has_uploaded && (
                    <span className="rounded-full bg-pme-lilac-soft px-2 py-0.5 text-[11px] font-semibold text-pme-navy">Uploaded file</span>
                  )}
                  <ChevronRight size={16} className="text-gray-400" aria-hidden />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
